/**
 * Chunk-load recovery utility.
 *
 * Browsers (and Cloudflare's HTML cache) can serve a stale `index.html` that
 * still references a previous deploy's `main.[hash].js`. When that bundle
 * tries to `import()` a CSS or JS chunk by its old content-hashed URL, the
 * new deploy doesn't have that file — Webpack throws `ChunkLoadError`.
 *
 * A plain `location.reload()` is NOT enough — the browser can return the
 * same cached HTML (referencing the same stale bundle) and loop forever.
 * We must:
 *   1. Throttle: at most one recovery per 30s per session
 *   2. Clear the service-worker caches + unregister the SW (the SW is
 *      configured to only cache `/error.html`, but a previous deploy
 *      may have registered a more aggressive SW that's still controlling
 *      this client).
 *   3. Force the next navigation to bust the HTML cache via a query-string
 *      timestamp.
 */
export const isChunkError = (error) => {
  if (!error) return false;
  const name = error.name || '';
  const msg = (error.message || String(error)) || '';
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('dynamically imported module') ||
    msg.includes("'text/html' is not a valid JavaScript MIME") // happens when CF returns HTML for a missing chunk URL
  );
};

export const recoverFromChunkError = async ({ force = false } = {}) => {
  const STORAGE_KEY = 'chunk_error_reload';
  const COOLDOWN_MS = 30_000;
  const now = Date.now();
  if (!force) {
    try {
      const last = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0', 10);
      if (now - last < COOLDOWN_MS) {
        // Already attempted recovery recently — let the ErrorBoundary
        // render its "New version available · Refresh Now" fallback so the
        // user can see what's happening instead of an infinite reload loop.
        return false;
      }
      sessionStorage.setItem(STORAGE_KEY, String(now));
    } catch (_e) {
      // sessionStorage disabled (private mode) — fall through and try once
    }
  } else {
    try { sessionStorage.setItem(STORAGE_KEY, String(now)); } catch (_e) { /* ignore */ }
  }

  // 1. Unregister all service workers so a stale one can't keep serving
  //    cached HTML/JS on the next load
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch (_e) { /* ignore */ }

  // 2. Drop every CacheStorage entry so the browser definitely refetches
  //    the HTML + new bundle from the network
  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch (_e) { /* ignore */ }

  // 3. Force a cache-busting navigation. Using a query string guarantees
  //    Cloudflare's HTML edge cache returns a fresh response, which will
  //    reference the new bundle hash.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(now));
    window.location.replace(url.toString());
  } catch (_e) {
    window.location.reload();
  }
  return true;
};
